import { Link } from "@inertiajs/react";
import { Cloud, Search, GitPullRequest } from "lucide-react";
import { DiffPreview, type DiffLine } from "./DiffPreview";

const STEPS = [
  {
    icon: Cloud,
    title: "1. Connect your infrastructure",
    description:
      "Add the Infralyst Terraform module, install the GitHub App, and map Terraform state to your AWS accounts and repos. Infralyst connects via a read-only IAM role. No agents to install, no write access to your infrastructure.",
  },
  {
    icon: Search,
    title: "2. Infralyst finds savings",
    description:
      "Infralyst continuously analyzes your AWS resources against conservative, transparent thresholds. No black-box scoring or machine learning. Every check is deterministic with documented rules.",
  },
  {
    icon: GitPullRequest,
    title: "3. Generate a Terraform PR",
    description:
      "Pick a recommendation and click Generate PR. An AI agent reads your Terraform repo, writes the change, and opens a pull request. Your team reviews, tests, and merges it like any other change.",
  },
] as const;

const DIFF_LINES: DiffLine[] = [
  { type: "hunk", content: "@@ -12,7 +12,7 @@" },
  { type: "context", content: 'resource "aws_instance" "api_prod" {', oldLineNumber: 12, newLineNumber: 12 },
  { type: "context", content: '  ami           = data.aws_ami.ubuntu.id', oldLineNumber: 13, newLineNumber: 13 },
  { type: "removed", content: '  instance_type = "m5.2xlarge"', oldLineNumber: 14 },
  { type: "added", content: '  instance_type = "m5.xlarge"', newLineNumber: 14 },
  { type: "context", content: "", oldLineNumber: 15, newLineNumber: 15 },
  { type: "context", content: "  tags = {", oldLineNumber: 16, newLineNumber: 16 },
];

export function HowItWorks(): React.ReactElement {
  return (
    <section id="how-it-works" className="py-16 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-4">
          How <span className="text-primary">Infralyst</span> Works
        </h2>

        <p className="text-center text-surface-foreground/80 mb-12">
          Three quick steps to go from AWS accounts to reviewable Terraform PRs.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {STEPS.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="group p-6 rounded-3xl border bg-background/50 hover:border-primary/20 hover:bg-primary/5 transition-colors"
            >
              <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Icon className="size-6" />
              </div>
              <h3 className="text-xl font-bold mb-3">{title}</h3>
              <p className="text-surface-foreground/80 text-sm leading-relaxed">
                {description}
              </p>
            </div>
          ))}
        </div>

        <div className="max-w-xl mx-auto mt-12">
          <p className="text-center text-sm text-surface-foreground/60 mb-4">
            Every change is a small, reviewable Terraform diff.
          </p>
          <DiffPreview filename="ec2.tf" lines={DIFF_LINES} />
        </div>

        <p className="text-center text-sm text-surface-foreground/60 mt-8">
          Want the full walkthrough?{" "}
          <Link href="/how-it-works" className="text-primary hover:underline">
            See how Infralyst works
          </Link>
        </p>
      </div>
    </section>
  );
}
